import * as React from 'react';
import { useState, useCallback } from 'react';

import { BoostInput, PFA_BOOST_TYPE, PFACatalogProductBoostInput, PFAFreeProductBoostInput } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  CircleInfoIcon,
} from '@revfluence/fresh-icons/solid/esm';
import { ArrowUpRightFromSquareIcon } from '@revfluence/fresh-icons/regular/esm';

import { useAppContext } from '@frontend/context/AppContext';
import { useGetCatalogs } from '@frontend/app/containers/Settings/ProductFulfillment/Catalogs/hooks/useGetCatalogs';
import { GetBrandCatalogsQuery_getBrandCatalogs } from '@frontend/applications/ProductFulfillmentApp/queries/types/GetBrandCatalogsQuery';
import { CatalogStatus } from '@frontend/applications/ProductFulfillmentApp/types/globalTypes';
import { useGetSelectionCriteria } from '@frontend/app/containers/Settings/ProductFulfillment/Catalogs/hooks/useGetSelectionCriteria';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@frontend/shadcn/components/ui/collapsible';
import { ApolloProvider } from '@apollo/client';
import { useGetCatalogStats } from '@frontend/app/containers/Settings/ProductFulfillment/Catalogs/hooks/useGetCatalogStats';
import { useHistory } from 'react-router-dom';
import { pfaV2Routes } from '@frontend/app/containers/Settings/ProductFulfillment/routes';
import { Button } from '@/shadcn/components/ui/button';
import { Label } from '@/shadcn/components/ui/label';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/shadcn/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shadcn/components/ui/select';

import { BoostSheetProps } from './BoostDrawer';
import { GiftProductsModal } from './GiftProductsModal';
import GiftProductsList from './GiftProductsList';

interface FreeProductShopifyBoostProps extends BoostSheetProps {
  draftBoost?: BoostInput;
  updateDraftBoost?: <K extends keyof BoostInput>(key: K, value: BoostInput[K]) => void;
}

export const FreeProductShopifyBoost = (
  props: FreeProductShopifyBoostProps,
): JSX.Element => {
  const { draftBoost, updateDraftBoost } = props;
  const { pfaApolloClient } = useAppContext();
  const { catalogs, loading: isCatalogsLoading } = useGetCatalogs({ client: pfaApolloClient });
  const [pfaBoostMetadata, setPfaBoostMetadata] = useState<PFACatalogProductBoostInput>(
    draftBoost?.boostMetadata || {}
  );
  const [pfaBoostType, setPfaBoostType] = useState<PFA_BOOST_TYPE>(
    draftBoost?.boostMetadata?.pfaBoostType || PFA_BOOST_TYPE.PRODUCT_CATALOG
  );
  const [selectedGiftVariants, setSelectedGiftVariants] = useState<PFAFreeProductBoostInput[]>(pfaBoostMetadata?.giftProducts || []);

  let activeBrandCatalogs: GetBrandCatalogsQuery_getBrandCatalogs[] = [];
  if (!isCatalogsLoading) {
    activeBrandCatalogs = catalogs?.filter((catalog) => catalog.status === CatalogStatus.ACTIVE);
  }

  const history = useHistory();

  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const selectedCatalog = pfaBoostMetadata?.catalogId ? activeBrandCatalogs.find((catalog) => catalog.id === pfaBoostMetadata.catalogId) : undefined;

  const { criteria: selectionRules, loading: isSelectionRulesLoading } = useGetSelectionCriteria({
    variables: {
      brandCatalogId: pfaBoostMetadata?.catalogId,
    },
    skip: !pfaBoostMetadata?.catalogId,
    client: pfaApolloClient,
  });

  const handleRemoveGiftVariant = (variantId: string) => {
    const updatedVariants = selectedGiftVariants.filter((variant) => variant.variantId !== variantId);
    setSelectedGiftVariants(updatedVariants);
  };

  const handlePFABoostMetadataChange = (key: keyof PFACatalogProductBoostInput, value: any) => {
    const updatedMetadata = { ...pfaBoostMetadata, [key]: value, pfaBoostType, giftProducts: selectedGiftVariants };
          // Update catalog name and selection rule name
    if (key === 'catalogId') {
      updatedMetadata.catalogName = selectedCatalog?.name;
      updatedMetadata.catalogSelectionRuleName = undefined; // reset the selection rule name
    }
    if (key === 'catalogSelectionRuleId') {
      updatedMetadata.catalogSelectionRuleName = selectionRules?.find(
        (rule) => Number(rule.id) === Number(value)
      )?.label;
    }
    setPfaBoostMetadata(updatedMetadata);
    updateDraftBoost?.('boostMetadata', updatedMetadata);
  };

  const { catalogStats, loading: isStatsLoading } = useGetCatalogStats({
    variables: {
      id: Number(pfaBoostMetadata?.catalogId),
    },
    skip: !selectedCatalog?.id,
    client: pfaApolloClient,
  });

  const updatePfaBoostType = (value: PFA_BOOST_TYPE) => {
    setPfaBoostType(value);
  };

    // Sync local state with draftBoost when it changes (for Edit mode)
  React.useEffect(() => {
    if (draftBoost) {
      // Sync pfaBoostMetadata - use existing metadata or empty object
      const metadata = draftBoost.boostMetadata || {};
      setPfaBoostMetadata(metadata);

      // Sync selected products
      const products = draftBoost.boostMetadata?.giftProducts || [];
      setSelectedGiftVariants(products);
    }
  }, [draftBoost]);

  // Update draftBoost when selectedProducts change
  React.useEffect(() => {
    // Only update if selectedGiftVariants has actually changed
    if (selectedGiftVariants.length > 0 && pfaBoostMetadata.giftProducts !== selectedGiftVariants) {
      const updatedMetadata = { ...pfaBoostMetadata, giftProducts: selectedGiftVariants, pfaBoostType };
      setPfaBoostMetadata(updatedMetadata);
      updateDraftBoost?.('boostMetadata', updatedMetadata);
    }
  }, [
    selectedGiftVariants,
    pfaBoostMetadata,
    pfaBoostType,
    updateDraftBoost,
  ]);

  const handleCatalogClick = React.useCallback(
    (id: string) => {
      history.push(pfaV2Routes.settings.brandCatalogsDetails.replace(':catalogId', id));
    },
    [history],
  );

  // Memoize the setSelectedGiftVariants function
  const memoizedSetSelectedGiftVariants = useCallback((variants: PFAFreeProductBoostInput[]) => {
    setSelectedGiftVariants(variants);
  }, []);

  return (
    <div className="p-4 bg-gray-50 rounded-2xl flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label className="text-xs font-medium text-gray-800">
          How creator receives product
        </Label>
        <RadioGroup
          value={pfaBoostType}
          onValueChange={updatePfaBoostType}
          className="flex gap-10"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value={PFA_BOOST_TYPE.PRODUCT_CATALOG} id="creator-selecting" />
            <Label htmlFor="creator-selecting" className="text-sm font-normal">
              Creator selecting products
            </Label>
            <CircleInfoIcon className="h-3 w-3 text-gray-300" />
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value={PFA_BOOST_TYPE.DIRECT_PRODUCT} id="gift-specific" />
            <Label htmlFor="gift-specific" className="text-sm font-normal">
              Gift Specific Products
            </Label>
            <CircleInfoIcon className="h-3 w-3 text-gray-300" />
          </div>
        </RadioGroup>
      </div>
      {pfaBoostType === PFA_BOOST_TYPE.PRODUCT_CATALOG && (
        <>
          <div className="flex gap-4">
            <div className="flex-1 flex flex-col">
              <div className="pb-2 flex items-center gap-1">
                <Label htmlFor="catalog-select" className="text-xs font-medium text-gray-800">
                  Select Catalog
                </Label>
                <CircleInfoIcon className="h-3 w-3 text-gray-300" />
              </div>
              <Select
                value={pfaBoostMetadata?.catalogId?.toString()}
                onValueChange={(value) => handlePFABoostMetadataChange('catalogId', Number(value))}
              >
                <SelectTrigger id="catalog-select">
                  <SelectValue placeholder="Select Catalog" />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  {activeBrandCatalogs?.map((catalog) => (
                    <SelectItem key={catalog.id} value={catalog.id.toString()}>
                      {catalog.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 flex flex-col">
              <div className="pb-2 flex items-center gap-1">
                <Label htmlFor="rule-select" className="text-xs font-medium text-gray-800">
                  Select Rule
                </Label>
                <CircleInfoIcon className="h-3 w-3 text-gray-300" />
              </div>
              <Select
                value={pfaBoostMetadata?.catalogSelectionRuleId?.toString()}
                onValueChange={(value) => handlePFABoostMetadataChange('catalogSelectionRuleId', Number(value))}
                disabled={!pfaBoostMetadata?.catalogId || isSelectionRulesLoading || !selectionRules?.length}
              >
                <SelectTrigger id="rule-select">
                  <SelectValue placeholder="Select Rule" />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  {selectionRules?.map((rule) => (
                    <SelectItem key={rule.id} value={rule.id.toString()}>
                      {rule.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex w-full items-center gap-2">
            <Collapsible open={isDetailsExpanded} onOpenChange={() => setIsDetailsExpanded(!isDetailsExpanded)} className="w-full">
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex justify-start w-full px-1 py-2 text-left hover:bg-grey-100 rounded-md"
                >
                  <Label className="justify-start text-gray-800 text-xs font-medium font-['Inter'] leading-tight">View Details</Label>
                  {isDetailsExpanded ? (
                    <ChevronUpIcon className="h-4 ml-2 w-4 text-gray-600" />
                  ) : (
                    <ChevronDownIcon className="h-4 ml-2 w-4 text-gray-600" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4 space-y-4 animate-collapsible-down">
                {selectedCatalog?.id && (
                  <div className="flex w-full flex-col gap-2">
                    <div className="flex justify-between items-center">
                      <div className="px-2 py-0.5 bg-gray-200 rounded-lg">
                        <p className="text-xs text-gray-800 font-normal">{selectedCatalog.name}</p>
                      </div>
                      <Button variant="link" className="text-xs font-medium text-gray-800 p-0 h-auto" asChild>
                        <a
                          href="#"
                          onClick={(e) => {
                          e.preventDefault();
                          handleCatalogClick(selectedCatalog.id.toString());
                        }}
                          rel="noopener noreferrer"
                        >
                          View Catalog
                          <ArrowUpRightFromSquareIcon className="w-3 h-3 ml-1" />
                        </a>
                      </Button>
                    </div>
                    {!isStatsLoading && catalogStats?.collectionCount && (
                      <div className="grid w-full grid-cols-3 gap-2.5">
                        <div className="p-3 bg-white rounded-xl border border-gray-200">
                          <p className="text-xs text-gray-500">Collections</p>
                          <p className="text-sm text-gray-800 font-medium">{catalogStats.collectionCount}</p>
                        </div>
                        <div className="p-3 bg-white rounded-xl border border-gray-200">
                          <p className="text-xs text-gray-500">Products</p>
                          <p className="text-sm text-gray-800 font-medium">{catalogStats.productCount}</p>
                        </div>
                        <div className="p-3 bg-white rounded-xl border border-gray-200">
                          <p className="text-xs text-gray-500">Variants</p>
                          <p className="text-sm text-gray-800 font-medium">{catalogStats.variantCount}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          </div>
        </>
      )}

      {pfaBoostType === PFA_BOOST_TYPE.DIRECT_PRODUCT && (
        <>
          <GiftProductsList selectedGiftVariants={selectedGiftVariants} handleRemoveGiftVariant={handleRemoveGiftVariant} />
          {/* <div className="flex flex-col gap-2">
            <Label className="text-xs font-medium text-gray-800">
              Select Product
            </Label>
          </div> */}
          <ApolloProvider client={pfaApolloClient}>
            <GiftProductsModal
              selectedGiftVariants={selectedGiftVariants}
              setSelectedGiftVariants={memoizedSetSelectedGiftVariants}
            />
          </ApolloProvider>
        </>
      )}
    </div>
  );
};
